import type { channelWa } from '@repletoryreach/sdk-api';

/**
 * What WhatsApp accepts on a business profile, checked on the page before
 * anything is sent (15 October 2026), so most refusals never reach WhatsApp.
 *
 * The same rules are enforced by channel-wa (`BusinessProfileValidator`) for
 * every caller, and the limits come from the service with the profile read;
 * these mirror it so the person sees the problem under the field as they
 * type, not after a round trip. Only what is about to be sent is checked: a
 * field nobody touched is not sent, so it cannot be refused.
 */

/** Meta's limits, as served with the read, with the picture sizes always present. */
export type ProfileLimits = channelWa.BusinessProfileLimits & { picture_min_edge: number; picture_edge: number };

/** Meta's limits stand in until the read lands, and for a server older than the picture sizes. */
export const FALLBACK_LIMITS: ProfileLimits = {
  about: 139,
  address: 256,
  description: 512,
  email: 128,
  website: 256,
  websites: 2,
  picture_bytes: 5 * 1024 * 1024,
  picture_min_edge: 192,
  picture_edge: 640
};

export function withFallback(limits: Partial<channelWa.BusinessProfileLimits> | undefined | null): ProfileLimits {
  return { ...FALLBACK_LIMITS, ...(limits ?? {}) } as ProfileLimits;
}

/** The field a problem is shown under. The two website boxes are separate fields on the page. */
export type CheckField = 'about' | 'description' | 'address' | 'email' | 'website1' | 'website2';

export type CheckCode = 'tooLong' | 'aboutEmpty' | 'emailInvalid' | 'websiteScheme' | 'websitesTooMany';

export interface ProfileCheck {
  field: CheckField;
  code: CheckCode;
  vars?: Record<string, number>;
}

export interface CheckInput {
  about: string;
  address: string;
  description: string;
  email: string;
  websites: string[];
}

/** What is about to be sent: the request the page builds from the changed fields. */
export type CheckChanges = Partial<Pick<channelWa.UpdateBusinessProfileRequest, 'about' | 'address' | 'description' | 'email' | 'websites'>>;

/**
 * Deliberately as loose as the service's: a missing @ or a typo in the domain
 * is caught; whether the address is deliverable is not this page's business.
 */
export function looksLikeEmail(s: string): boolean {
  const at = s.indexOf('@');
  if (at <= 0 || at === s.length - 1) return false;
  if (s.indexOf('@', at + 1) >= 0) return false;
  const domain = s.slice(at + 1);
  return domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.') && !/\s/.test(s);
}

/** Meta requires the scheme and a full domain: "example.co.za" alone is refused there. */
export function looksLikeWebsite(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.');
  } catch {
    return false;
  }
}

export function checkProfile(fields: CheckInput, changes: CheckChanges, limits: ProfileLimits): ProfileCheck[] {
  const out: ProfileCheck[] = [];
  const tooLong = (field: CheckField, used: number, max: number) => out.push({ field, code: 'tooLong', vars: { used, max } });

  if (changes.about !== undefined) {
    if (fields.about.length > limits.about) tooLong('about', fields.about.length, limits.about);
    // Meta's one asymmetry: the status line may not be sent empty.
    else if (fields.about.trim().length === 0) out.push({ field: 'about', code: 'aboutEmpty' });
  }
  if (changes.description !== undefined && fields.description.length > limits.description) {
    tooLong('description', fields.description.length, limits.description);
  }
  if (changes.address !== undefined && fields.address.length > limits.address) {
    tooLong('address', fields.address.length, limits.address);
  }
  if (changes.email !== undefined) {
    const email = fields.email.trim();
    if (email.length > limits.email) tooLong('email', email.length, limits.email);
    else if (email.length > 0 && !looksLikeEmail(email)) out.push({ field: 'email', code: 'emailInvalid' });
  }
  if (changes.websites !== undefined) {
    const boxes: CheckField[] = ['website1', 'website2'];
    const filled = fields.websites.map((w) => w.trim()).filter(Boolean);
    fields.websites.forEach((raw, i) => {
      const url = raw.trim();
      const field = boxes[i];
      if (!url || !field) return;
      if (url.length > limits.website) tooLong(field, url.length, limits.website);
      else if (!looksLikeWebsite(url)) out.push({ field, code: 'websiteScheme' });
    });
    if (filled.length > limits.websites) out.push({ field: 'website1', code: 'websitesTooMany', vars: { max: limits.websites } });
  }
  return out;
}

/** The first problem per field, which is the one shown under it. */
export function firstPerField(checks: ProfileCheck[]): Partial<Record<CheckField, ProfileCheck>> {
  const out: Partial<Record<CheckField, ProfileCheck>> = {};
  for (const c of checks) if (!out[c.field]) out[c.field] = c;
  return out;
}
